import React, { useState, useCallback, useContext } from 'react';
import { useAriaLive } from '@ally/use-aria-live';
import whisper from '../../whisper';
import { a11y } from '../strings';
const SteppedProcessContext = React.createContext({});
const SteppedProcessConsumer = SteppedProcessContext.Consumer;
const displayError = (functionName, errorMessage) => {
    whisper.error({
        message: `SteppedProcessProvider:${functionName}:\n${errorMessage}`,
    });
};
const SteppedProcessProvider = ({ initialStepsMetadata, initialActiveStepIndex, children }) => {
    const [stepsMetadata, setStepsMetadata] = useState(initialStepsMetadata || []);
    const [activeStep, setActiveStep] = useState((initialStepsMetadata &&
        initialActiveStepIndex !== undefined &&
        initialStepsMetadata[initialActiveStepIndex]) ||
        undefined);
    const [focusStep, setFocusStep] = useState(-1);
    const getAriaLive = a11y.getAriaLiveSetNextStepActive;
    const { announceAssertive } = useAriaLive();
    const isOutsideRange = useCallback((stepIndex) => {
        return stepIndex >= stepsMetadata.length || stepIndex < 0;
    }, [stepsMetadata]);
    const setActiveStepImpl = useCallback((stepIndex) => {
        if (isOutsideRange(stepIndex)) {
            displayError('setActiveStep', 'index is out of range - returning without executing request');
            return;
        }
        setActiveStep(stepsMetadata[stepIndex]);
        // allow step to mount before setting focus
        setTimeout(() => setFocusStep(stepIndex), 0);
    }, [stepsMetadata, setActiveStep, isOutsideRange]);
    const deactivateActiveStep = useCallback(() => {
        setActiveStep(undefined);
    }, [setActiveStep]);
    const setStepState = useCallback((stepIndex, stepState) => {
        setStepsMetadata(stepsMetadata.map((step, index) => {
            if (index === stepIndex) {
                return Object.assign(Object.assign({}, step), { state: stepState });
            }
            return step;
        }));
    }, [stepsMetadata, setStepsMetadata]);
    const setNextStepActive = useCallback(() => {
        if (!activeStep)
            return;
        const { index } = activeStep;
        const stepCount = stepsMetadata.length;
        const nextIndex = index < stepCount - 1 ? index + 1 : stepCount - 1;
        setStepState(activeStep.index, 'complete');
        setActiveStepImpl(nextIndex);
        const currentStep = index + 1;
        const currentTitle = activeStep.title;
        const nextStep = nextIndex + 1;
        const nextTitle = stepsMetadata[nextIndex].title;
        announceAssertive(getAriaLive(currentStep, currentTitle, nextStep, nextTitle));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeStep,
        stepsMetadata,
        setStepState,
        setActiveStepImpl,
        announceAssertive,
    ]);
    const setPrevStepActive = useCallback(() => {
        if (!activeStep)
            return;
        const { index } = activeStep;
        let prevIndex = typeof index === 'number' ? index - 1 : 0;
        if (prevIndex < 0)
            prevIndex = 0;
        setActiveStepImpl(prevIndex);
    }, [activeStep, setActiveStepImpl]);
    const isStepActive = useCallback((stepIndex) => {
        if (isOutsideRange(stepIndex)) {
            displayError('isStepActive', 'index is not in range - returning undefined');
            return undefined;
        }
        return (activeStep === null || activeStep === void 0 ? void 0 : activeStep.index) === stepIndex;
    }, [activeStep, isOutsideRange]);
    const isStepComplete = useCallback((stepIndex) => {
        if (isOutsideRange(stepIndex)) {
            displayError('isStepComplete', 'index is not in range - returning undefined');
            return undefined;
        }
        return stepsMetadata[stepIndex].state === 'complete';
    }, [stepsMetadata, isOutsideRange]);
    const stepState = useCallback((stepIndex) => {
        if (isOutsideRange(stepIndex)) {
            displayError('stepState', 'index is not in range - returning undefined');
            return undefined;
        }
        return stepsMetadata[stepIndex].state;
    }, [stepsMetadata, isOutsideRange]);
    return (React.createElement(SteppedProcessContext.Provider, { value: {
            stepsMetadata,
            activeStep,
            focusStep,
            setActiveStep: setActiveStepImpl,
            setNextStepActive,
            setPrevStepActive,
            setStepState,
            deactivateActiveStep,
            isStepActive,
            isStepComplete,
            stepState,
        } }, children));
};
const useSteppedProcess = () => {
    return useContext(SteppedProcessContext);
};
export { SteppedProcessProvider, SteppedProcessConsumer, useSteppedProcess };
//# sourceMappingURL=SteppedProcessProvider.js.map